import { WEEKDAY_LABELS } from '@crm/shared';
import { Button, Checkbox, Input, Spinner } from '@crm/ui';
import { Copy } from 'lucide-react';
import { useEffect, useState } from 'react';
import { toErrorMessage } from '@/modules/auth/utils';
import { useProfessionalSchedule, useSaveProfessionalSchedule, type WorkDay } from '../api';

interface Linha {
  atende: boolean;
  startTime: string;
  endTime: string;
  breakStart: string;
  breakEnd: string;
}

const PADRAO: Linha = {
  atende: false,
  startTime: '09:00',
  endTime: '18:00',
  breakStart: '',
  breakEnd: '',
};

function daBase(dias: WorkDay[]): Linha[] {
  return WEEKDAY_LABELS.map((_, indice) => {
    const dia = dias.find((item) => item.weekday === indice);

    return dia
      ? {
          atende: true,
          startTime: dia.startTime,
          endTime: dia.endTime,
          breakStart: dia.breakStart ?? '',
          breakEnd: dia.breakEnd ?? '',
        }
      : { ...PADRAO };
  });
}

/**
 * Os dias em que a pessoa atende. Sem nenhum dia marcado a agenda dela fica
 * cheia (e o passo de configuracao cobra isso); com dias marcados, so o que
 * esta aqui e oferecido ao cliente.
 */
export function ScheduleEditor({ professionalId }: { professionalId: string }) {
  const horarios = useProfessionalSchedule(professionalId);
  const salvar = useSaveProfessionalSchedule(professionalId);

  const [linhas, setLinhas] = useState<Linha[]>(() => daBase([]));

  useEffect(() => {
    if (horarios.data) setLinhas(daBase(horarios.data.data));
  }, [horarios.data]);

  function altera(indice: number, valores: Partial<Linha>) {
    setLinhas((atual) =>
      atual.map((linha, posicao) => (posicao === indice ? { ...linha, ...valores } : linha)),
    );
  }

  /** O caso comum e a semana toda igual - digitar sete vezes e trabalho a toa. */
  function copiarParaTodos(indice: number) {
    const modelo = linhas[indice];
    if (!modelo) return;

    setLinhas((atual) => atual.map((linha) => (linha.atende ? { ...modelo } : linha)));
  }

  function envia() {
    const dias: WorkDay[] = linhas.flatMap((linha, indice) =>
      linha.atende
        ? [
            {
              weekday: indice,
              startTime: linha.startTime,
              endTime: linha.endTime,
              breakStart: linha.breakStart || null,
              breakEnd: linha.breakEnd || null,
            },
          ]
        : [],
    );

    salvar.mutate(dias);
  }

  if (horarios.isPending) {
    return (
      <div className="flex justify-center py-8">
        <Spinner />
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {salvar.error ? (
        <p className="text-sm text-destructive">{toErrorMessage(salvar.error)}</p>
      ) : null}

      <ul className="space-y-2">
        {linhas.map((linha, indice) => (
          <li
            key={WEEKDAY_LABELS[indice]}
            className="flex flex-wrap items-center gap-2 rounded-card border border-border px-3 py-2"
          >
            <Checkbox
              className="w-24 shrink-0"
              label={WEEKDAY_LABELS[indice]}
              checked={linha.atende}
              onChange={(input) => altera(indice, { atende: input.target.checked })}
            />

            {linha.atende ? (
              <>
                <Input
                  type="time"
                  className="w-28"
                  aria-label={`Inicio ${WEEKDAY_LABELS[indice]}`}
                  value={linha.startTime}
                  onChange={(input) => altera(indice, { startTime: input.target.value })}
                />
                <span className="text-sm text-muted-foreground">as</span>
                <Input
                  type="time"
                  className="w-28"
                  aria-label={`Fim ${WEEKDAY_LABELS[indice]}`}
                  value={linha.endTime}
                  onChange={(input) => altera(indice, { endTime: input.target.value })}
                />

                <span className="hidden text-sm text-muted-foreground sm:inline">intervalo</span>
                <Input
                  type="time"
                  className="w-28"
                  aria-label={`Inicio do intervalo ${WEEKDAY_LABELS[indice]}`}
                  value={linha.breakStart}
                  onChange={(input) => altera(indice, { breakStart: input.target.value })}
                />
                <Input
                  type="time"
                  className="w-28"
                  aria-label={`Fim do intervalo ${WEEKDAY_LABELS[indice]}`}
                  value={linha.breakEnd}
                  onChange={(input) => altera(indice, { breakEnd: input.target.value })}
                />

                <Button
                  size="sm"
                  variant="ghost"
                  title="Copiar para os outros dias marcados"
                  onClick={() => copiarParaTodos(indice)}
                >
                  <Copy className="h-4 w-4" />
                </Button>
              </>
            ) : (
              <span className="text-sm text-muted-foreground">Nao atende</span>
            )}
          </li>
        ))}
      </ul>

      <div className="flex justify-end">
        <Button onClick={envia} disabled={salvar.isPending}>
          {salvar.isPending ? <Spinner /> : null}
          Salvar horarios
        </Button>
      </div>
    </div>
  );
}
